import { useHeaderStateProvider2 } from '@/context/HeaderStateContext2';
import { DownOutlined, RightOutlined } from '@ant-design/icons';
import { Button, Divider, Layout, Radio, Space, TreeSelect } from 'antd';
import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { useLocation } from 'react-router-dom';
import FullscreenButton from '../screenToggle';
import DateRangePicker from '../date-range-picker/DateRangePicker';
import { ProfileDropDown } from '../layout/ProfileDropDown';
import CountrySwitcher from '../country/CountrySwitcher';
import NotificationBell from '../notifications/NotificationBell';
import { persistGet, persistSet } from '@/lib/utils';
import GlobalSearch from '../GlobalSearch';

const { Header } = Layout;

export interface Account {
  id: number;
  createdAt: string; // ISO date string
  updatedAt: string; // ISO date string
  userId: number;
  profileId: string;
  countryCode: string;
  currencyCode: string;
  dailyBudget: number | null;
  timezone: string;
  marketplaceId: string;
  accountId: string;
  accountType: 'vendor' | 'seller' | string; // extend if more types exist
  accountName: string;
  isMarketingStreamActivate: boolean;
  validPaymentMethod: boolean;
  linkedAccountId: number;
  selected: boolean;
}

const DashboardHeader2: React.FC = () => {
  const {
    adAccounts,
    setAdAccounts,
    adAccountVisible,
    setAdAccountVisible,
    setAdvertiserId,
    setProfileId,
    setLinkedAccountId,
  } = useHeaderStateProvider2();

  const [tempValue, setTempValue] = useState<string | undefined>(undefined);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const [expandedAccounts, setExpandedAccounts] = useState<Set<number>>(
    new Set()
  );
  const location = useLocation();
  // Use a separate persistence key for header2 to avoid clobbering multi-select
  const HEADER2_PROFILE_KEY = 'app_v2_header2_profile';
  // One-shot guard to prevent hydrate effects from looping
  const hasHydratedRef = useRef(false);

  // Filter accounts to only include profiles with isMarketingStreamActivate: true
  const filteredAccounts = useMemo(() => {
    return adAccounts
      .map((account) => ({
        ...account,
        profiles: account.profiles.filter(
          (profile) => profile.isMarketingStreamActivate
        ),
      }))
      .filter((account) => account.profiles.length > 0);
  }, [adAccounts]);

  const toggleAccountExpansion = useCallback((accountId: number) => {
    setExpandedAccounts((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(accountId)) {
        newSet.delete(accountId);
      } else {
        newSet.add(accountId);
      }
      return newSet;
    });
  }, []);

  useEffect(() => {
    if (
      filteredAccounts.length > 0 &&
      !hasUserInteracted &&
      !hasHydratedRef.current
    ) {
      // 1) Try to hydrate from header2's own persisted profile selection
      try {
        const stored = persistGet<string[]>(HEADER2_PROFILE_KEY);
        if (stored && Array.isArray(stored) && stored.length > 0) {
          const match = filteredAccounts
            .flatMap((account) => account.profiles)
            .find((p) => stored.includes(p.profileId)) as unknown as
            | Account
            | undefined;
          if (match) {
            const updatedAccounts = filteredAccounts.map((account) => ({
              ...account,
              selected: account.profiles.some(
                (p) => p.profileId === match.profileId
              ),
              profiles: account.profiles.map((profile) => ({
                ...profile,
                selected: profile.profileId === match.profileId,
              })),
            }));
            setAdAccounts(updatedAccounts);
            setAdvertiserId([match.accountId]);
            setProfileId(match.profileId);
            setLinkedAccountId(match.linkedAccountId);
            setTempValue(match.accountId);
            hasHydratedRef.current = true;
            return; // Do not run defaulting logic
          }
        }
      } catch {}

      // 2) Fall back to any pre-selected in data, else first profile
      const selectedProfile = filteredAccounts
        .flatMap((account) => account.profiles)
        .find((profile) => profile.selected) as unknown as Account;

      if (selectedProfile) {
        setAdvertiserId([selectedProfile.accountId]);
        setProfileId(selectedProfile.profileId);
        setLinkedAccountId(selectedProfile.linkedAccountId);
        setTempValue(selectedProfile.accountId);
        // Persist to header2-specific key (single selection)
        persistSet(HEADER2_PROFILE_KEY, [selectedProfile.profileId]);
        hasHydratedRef.current = true;
      } else {
        const firstProfile = filteredAccounts[0]
          .profiles[0] as unknown as Account;
        if (firstProfile) {
          const updatedAccounts = filteredAccounts.map((account) => ({
            ...account,
            selected: account.id === filteredAccounts[0].id,
            profiles: account.profiles.map((profile) => ({
              ...profile,
              selected: profile.accountId === firstProfile.accountId,
            })),
          }));
          setAdAccounts(updatedAccounts);
          setAdvertiserId([firstProfile.accountId]);
          setProfileId(firstProfile.profileId);
          setLinkedAccountId(firstProfile.linkedAccountId);
          setTempValue(firstProfile.accountId);
          // Persist to header2-specific key (single selection)
          persistSet(HEADER2_PROFILE_KEY, [firstProfile.profileId]);
          hasHydratedRef.current = true;
        }
      }
    }
  }, [
    filteredAccounts,
    hasUserInteracted,
    setAdAccounts,
    setAdvertiserId,
    setLinkedAccountId,
    setProfileId,
  ]);

  useEffect(() => {
    if (
      filteredAccounts.length &&
      !tempValue &&
      !hasUserInteracted &&
      !hasHydratedRef.current
    ) {
      const preferredAccountId = 'ENTITY1CBYE2OF4DTD9';
      const matchedProfile = filteredAccounts
        .flatMap((account) => account.profiles)
        .find((profile) => profile.accountId === preferredAccountId);

      const selectedProfile = filteredAccounts
        .flatMap((account) => account.profiles)
        .find((profile) => profile.selected);

      const defaultSelected =
        selectedProfile?.accountId ||
        matchedProfile?.accountId ||
        filteredAccounts[0]?.profiles[0]?.accountId;

      if (defaultSelected) {
        setTempValue(defaultSelected);
        if (!selectedProfile) {
          const updatedAccounts = filteredAccounts.map((account) => ({
            ...account,
            selected: account.profiles.some(
              (p) => p.accountId === defaultSelected
            ),
            profiles: account.profiles.map((profile) => ({
              ...profile,
              selected: profile.accountId === defaultSelected,
            })),
          }));
          setAdAccounts(updatedAccounts);
          hasHydratedRef.current = true;
        }
      }
    }
  }, [filteredAccounts, hasUserInteracted, setAdAccounts, tempValue]);

  const getPageTitle = useCallback(() => {
    const path = location.pathname.split('/').pop() || '';
    return path
      .split('-')
      .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
      .join(' ');
  }, [location.pathname]);

  const treeData = useMemo(() => {
    return filteredAccounts.map((account) => ({
      title: `${account.name} (${account.profiles.length})`,
      value: `account_${account.id}`,
      selectable: true,
      children: account.profiles.map((profile) => ({
        title: profile.accountName,
        value: profile.accountId,
      })),
    }));
  }, [filteredAccounts]);

  const onApply = useCallback(() => {
    if (!tempValue) return;

    const updatedAccounts = filteredAccounts.map((account) => {
      const isAccountSelected = tempValue === `account_${account.id}`;
      const selectedProfile = account.profiles.find(
        (profile) => profile.accountId === tempValue
      );

      setAdvertiserId([tempValue]);
      if (isAccountSelected) {
        const firstProfile = account.profiles[0];
        return {
          ...account,
          selected: true,
          profiles: account.profiles.map((profile) => ({
            ...profile,
            selected: profile.accountId === firstProfile?.accountId,
          })),
        };
      } else if (selectedProfile) {
        return {
          ...account,
          selected: true,
          profiles: account.profiles.map((profile) => ({
            ...profile,
            selected: profile.accountId === tempValue,
          })),
        };
      }
      return {
        ...account,
        selected: false,
        profiles: account.profiles.map((profile) => ({
          ...profile,
          selected: false,
        })),
      };
    });

    setAdAccounts(updatedAccounts);
    const selectedProfile = updatedAccounts
      .flatMap((account) => account.profiles)
      .find((profile) => profile.selected) as unknown as Account;

    if (selectedProfile) {
      setTempValue(selectedProfile.accountId);
      setProfileId(selectedProfile.profileId);
      setLinkedAccountId(selectedProfile.linkedAccountId);
      // Persist selected profileId for header2 (single)
      persistSet(HEADER2_PROFILE_KEY, [selectedProfile.profileId]);
    }
    setAdAccountVisible(false);
  }, [
    tempValue,
    filteredAccounts,
    setAdAccounts,
    setAdAccountVisible,
    setAdvertiserId,
    setProfileId,
    setLinkedAccountId,
  ]);

  const getSelectedProfileName = useCallback(() => {
    if (!tempValue) return 'Select Account';
    const selectedProfile = filteredAccounts
      .flatMap((account) => account.profiles)
      .find((profile) => profile.accountId === tempValue);
    return selectedProfile ? selectedProfile.accountName : 'Select Account';
  }, [filteredAccounts, tempValue]);

  const collapseTriggerStyle: React.CSSProperties = {
    backgroundColor: '#232f3e',
    color: 'rgba(255, 255, 255, 0.65)',
    borderTop: '1px solid rgba(255, 255, 255, 0.1)',
    height: '48px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'color 0.3s',
  };

  return (
    <Header
      style={{
        ...collapseTriggerStyle,
        padding: '0 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <h2
        style={{
          margin: 0,
          fontSize: '1.2rem',
          fontWeight: 500,
          lineHeight: '1.5715',
          textTransform: 'uppercase',
          color: '#fff',
          letterSpacing: '1.5px',
        }}
      >
        {getPageTitle()}
      </h2>

      <div className="flex items-center gap-4">
        <FullscreenButton />

        {/* Global Search */}
        <GlobalSearch placeholder="Search pages and features..." />

        <Space size="large">
          {/* <FullscreenButton /> */}
          <TreeSelect
            className="custom-treeselect"
            style={{ width: 250 }}
            value={tempValue}
            styles={{ popup: { root: { maxHeight: 400, overflow: 'auto' } } }}
            treeData={treeData}
            placeholder={getSelectedProfileName()}
            treeDefaultExpandAll={false}
            onChange={() => {}} // Prevent default onChange
            onSelect={() => {}} // Prevent default onSelect
            open={adAccountVisible}
            onOpenChange={(visible) => {
              // Avoid redundant state updates to prevent render loops
              if (visible !== adAccountVisible) setAdAccountVisible(visible);
            }}
            popupRender={() => (
              <>
                <div style={{ padding: '8px 0' }}>
                  <Radio.Group
                    value={tempValue}
                    onChange={(e) => {
                      setTempValue(e.target.value);
                      setHasUserInteracted(true);
                    }}
                    style={{ width: '100%' }}
                  >
                    {filteredAccounts.map((account) => {
                      const isExpanded = expandedAccounts.has(account.id);
                      return (
                        <div key={account.id} style={{ marginBottom: 8 }}>
                          <div
                            style={{
                              fontWeight: 600,
                              padding: '8px 12px',
                              backgroundColor: '#f5f5f5',
                              fontSize: '13px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              userSelect: 'none',
                            }}
                            onClick={() => toggleAccountExpansion(account.id)}
                          >
                            {isExpanded ? (
                              <DownOutlined
                                style={{ fontSize: '10px', color: '#666' }}
                              />
                            ) : (
                              <RightOutlined
                                style={{ fontSize: '10px', color: '#666' }}
                              />
                            )}
                            {account.name} ({account.profiles.length})
                          </div>
                          {isExpanded &&
                            account.profiles.map((profile) => (
                              <div
                                key={profile.accountId}
                                style={{ padding: '4px 32px' }}
                              >
                                <Radio value={profile.accountId}>
                                  {profile.accountName}
                                  <br /> <strong>{profile.profileId}</strong>
                                </Radio>
                              </div>
                            ))}
                        </div>
                      );
                    })}
                  </Radio.Group>
                </div>
                <Divider style={{ margin: '4px 0' }} />
                <div style={{ padding: 8 }}>
                  <Button type="primary" block onClick={onApply}>
                    Apply
                  </Button>
                </div>
              </>
            )}
          />
          <DateRangePicker />
        </Space>
        <CountrySwitcher />
        <NotificationBell />
        <ProfileDropDown />
      </div>
    </Header>
  );
};

export default DashboardHeader2;
